<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Workflow Email Templates" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="add"
            color="primary"
            text="Add"
            :routeName="workflowEmailTemplateCreateRoute.name"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <v-row class="px-5 pb-2" align="center">
        <v-col cols="12" sm="4" class="pb-0">
          <v-text-field
            v-model="filterName"
            label="Name"
            clearable
            outlined
          />
        </v-col>
        <v-col cols="12" sm="4" class="pb-0">
          <BusinessEntitiesDropDownList
            v-model="filterBusinessEntityId"
            :clearable="true"
            @businessEntityIdChange="handleBusinessEntityChange"
          />
        </v-col>
        <v-col cols="12" sm="4" class="pb-0">
          <ClientsDropDownList
            :id="filterClientId"
            :businessEntityId="filterBusinessEntityId"
            :clearable="true"
            @clientIdChange="filterClientId = $event"
          />
        </v-col>
        <v-col cols="12" sm="4" class="pb-0">
          <v-switch
            v-model="filterIsActive"
            :label="filterIsActive ? 'Active' : 'Inactive'"
            class="mt-0"
            hide-details
            @change="loadTemplates"
          />
        </v-col>
      </v-row>
      <v-row justify="end">
        <v-col cols="12" md="6">
          <FilterButtons
            @search="loadTemplates"
            @clear="handleClearClick"
          ></FilterButtons>
        </v-col>
      </v-row>
      <FilterButtonsSmall
        @search="loadTemplates"
        @clear="handleClearClick"
      ></FilterButtonsSmall>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="workflowEmailTemplateList"
          class="elevation-2"
        >
          <template v-slot:item.isActive="{ item }">
            <v-simple-checkbox
              v-model="item.isActive"
              disabled
            />
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              class="mx-1 my-1"
              color="primary"
              outlined
              @click="editTemplate(item)"
            >
              <v-icon>edit</v-icon>
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-1 my-1"
                  color="primary"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  @click="copyTemplate(item)"
                >
                  <v-icon>content_copy</v-icon>
                </v-btn>
              </template>
              <span>Copy Template</span>
            </v-tooltip>
            <v-tooltip v-if="item.isActive" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-1 my-1"
                  color="red"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  @click="deactivateTemplate(item)"
                >
                  <v-icon>block</v-icon>
                </v-btn>
              </template>
              <span>Deactivate</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <ConfirmationModal
      title="Deactivate Template"
      :contentText="templateToDeactivate ? `Deactivate template &quot;${templateToDeactivate.name}&quot;? It will no longer appear in active templates.` : ''"
      :showModal="showDeactivateConfirmation"
      toolbarColor="primary"
      okText="Deactivate"
      @ok-click="handleDeactivateConfirmed"
      @cancel-click="showDeactivateConfirmation = false; templateToDeactivate = null"
    />
    <CopyWorkflowEmailTemplateDialog
      v-if="showCopyDialog"
      :template="selectedTemplate"
      @close="showCopyDialog = false"
      @copied="handleCopied"
    />
    <BottomSpacer />
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { workflowEmailTemplateCreateRoute, workflowEmailTemplateEditRoute } from '@/lib/constants/routes'
import { ButtonCustomIcon, BottomSpacer, FilterButtons, FilterButtonsSmall } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { BusinessEntitiesDropDownList } from '@/components/businessEntities'
import { ClientsDropDownList } from '@/components/clients'
import CopyWorkflowEmailTemplateDialog from './CopyWorkflowEmailTemplateDialog'
import { ConfirmationModal } from '@/components/dialogs'
import { notifySuccess } from '@/lib/notifications'

export default {
  components: {
    ButtonCustomIcon,
    BottomSpacer,
    SimplePageHeader,
    BusinessEntitiesDropDownList,
    ClientsDropDownList,
    FilterButtons,
    FilterButtonsSmall,
    CopyWorkflowEmailTemplateDialog,
    ConfirmationModal
  },
  data () {
    return {
      workflowEmailTemplateCreateRoute,
      filterName: null,
      filterBusinessEntityId: null,
      filterClientId: null,
      filterIsActive: true,
      showCopyDialog: false,
      selectedTemplate: null,
      showDeactivateConfirmation: false,
      templateToDeactivate: null,
      headers: [
        { text: 'Name', value: 'name', sortable: true },
        { text: 'Client', value: 'clientName', sortable: true, width: '25%' },
        { text: 'Recipient Source', value: 'recipientSourceName', sortable: true, width: '20%' },
        { text: 'Active', value: 'isActive', align: 'center', sortable: false, width: '10%' },
        { text: 'Actions', value: 'action', align: 'center', sortable: false, width: '15%' }
      ]
    }
  },
  computed: {
    ...mapFields('workflowEmailTemplates', ['workflowEmailTemplateList'])
  },
  methods: {
    ...mapActions('workflowEmailTemplates', ['getWorkflowEmailTemplates', 'deactivateWorkflowEmailTemplate']),
    ...mapActions('workflowEmailTemplate', ['copyWorkflowEmailTemplate']),
    loadTemplates () {
      const filters = { isActive: this.filterIsActive }
      if (this.filterName) filters.name = this.filterName
      if (this.filterBusinessEntityId) filters.businessEntityId = this.filterBusinessEntityId
      if (this.filterClientId) filters.clientId = this.filterClientId
      this.getWorkflowEmailTemplates(filters)
    },
    handleBusinessEntityChange () {
      this.filterClientId = null
    },
    handleClearClick () {
      this.filterName = null
      this.filterBusinessEntityId = null
      this.filterClientId = null
      this.filterIsActive = true
      this.loadTemplates()
    },
    editTemplate (template) {
      this.$router.push({ name: workflowEmailTemplateEditRoute.name, params: { id: template.id } })
    },
    copyTemplate (template) {
      this.selectedTemplate = template
      this.showCopyDialog = true
    },
    handleCopied (newName) {
      this.showCopyDialog = false
      this.copyWorkflowEmailTemplate({ sourceTemplateId: this.selectedTemplate.id, name: newName, clientId: this.selectedTemplate.clientId })
        .then(() => {
          notifySuccess(this.$store.dispatch, 'Template copied')
          this.loadTemplates()
        })
        .catch(() => {})
    },
    deactivateTemplate (template) {
      this.templateToDeactivate = template
      this.showDeactivateConfirmation = true
    },
    handleDeactivateConfirmed () {
      this.showDeactivateConfirmation = false
      const template = this.templateToDeactivate
      this.templateToDeactivate = null
      this.deactivateWorkflowEmailTemplate(template)
        .then(() => {
          notifySuccess(this.$store.dispatch, 'Template deactivated')
          this.loadTemplates()
        })
        .catch(() => {})
    }
  },
  created () {
    this.loadTemplates()
  }
}
</script>
